import { CSSProperties, FC, KeyboardEvent, useId, useState } from 'react';
import classNames from 'classnames';

import { Option } from '@gn-audio/jss-core/common/models';

export interface ColorFilterProps {
  options: Option[];
  selected?: Option | Option[];
  onSelect?: (option: Option) => void;
  dotSize?: number;
  showColorTitle?: boolean;
  containerClassName?: string;
}

const getDotBackgroundStyles = (colorString: string): CSSProperties => {
  const [firstColor, secondColor] = colorString.split('|');
  if (!secondColor) return { backgroundColor: `#${firstColor}` };
  const gradient = `linear-gradient(45deg, #${firstColor} 0%, #${firstColor} 50%, #${secondColor} 50%, #${secondColor} 100%)`;
  return { backgroundImage: gradient };
};

export const ColorFilter: FC<ColorFilterProps> = ({
  options,
  selected,
  onSelect,
  dotSize = 24,
  showColorTitle = true,
  containerClassName,
}) => {
  const [hoverColor, setHoverColor] = useState<Option | undefined>();
  const colorGroupName = useId();
  const isMultiplePicker = Array.isArray(selected);
  const buttonSize = dotSize + 12;

  const handleSelect = (option: Option) => {
    onSelect && onSelect(option);
  };

  const handleFocusColor = (option?: Option) => {
    setHoverColor(option);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>, option: Option) => {
    if (isMultiplePicker && event.key === 'Enter') {
      event.preventDefault();
      handleSelect(option);
    }
  };

  return (
    <>
      <ul
        className={classNames('flex gap-3', containerClassName)}
        role={isMultiplePicker ? 'group' : 'radiogroup'}
      >
        {options.map((option) => {
          const dotStyles = getDotBackgroundStyles(option.value);
          let isOptionChecked: boolean;

          if (isMultiplePicker) {
            isOptionChecked = selected.some((color) => color.value === option.value);
          } else {
            isOptionChecked = selected?.value === option.value;
          }
          return (
            <li key={option.value} role="presentation">
              <label
                style={{ width: buttonSize, height: buttonSize }}
                className={classNames(
                  'flex cursor-pointer items-center justify-center rounded-full transition-colors duration-200 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-25 has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:-outline-offset-2 has-[:focus-visible]:outline-focus',
                  isOptionChecked && 'bg-black bg-opacity-40'
                )}
                title={option.title}
                onMouseEnter={() => handleFocusColor(option)}
                onMouseLeave={() => handleFocusColor(undefined)}
              >
                <input
                  className="sr-only"
                  type={isMultiplePicker ? 'checkbox' : 'radio'}
                  name={colorGroupName}
                  disabled={option.disabled}
                  checked={isOptionChecked}
                  onChange={() => handleSelect(option)}
                  onKeyDown={(event) => handleKeyDown(event, option)}
                  onFocus={() => handleFocusColor(option)}
                  onBlur={() => handleFocusColor(undefined)}
                />
                <div
                  className="block rounded-full bg-blue-700 p-0 outline outline-2 outline-white content-['s']"
                  style={{ ...dotStyles, width: dotSize, height: dotSize }}
                ></div>
                <span className="sr-only">{option.title}</span>
              </label>
            </li>
          );
        })}
      </ul>
      {showColorTitle && !isMultiplePicker && (
        <p className="mb-4 mt-2 text-[0.75rem] text-gray-700">
          {hoverColor?.title ?? selected?.title}
        </p>
      )}
    </>
  );
};
